import * as THREE from 'three';

export interface BuildingConfig {
  width: number;
  depth: number;
  height: number;
  position: THREE.Vector3;
  style?: 'standard' | 'tower' | 'warehouse' | 'skyscraper';
}

export interface BuildingMeshes {
  building: THREE.Mesh;
  edges: THREE.LineSegments;
  details: THREE.Group;
}

// Neon color palette
const NEON_COLORS = [
  0x00ffff, // Cyan
  0xff00ff, // Magenta
  0x8000ff, // Purple
  0xff0080, // Pink
  0x00ff80, // Mint
];

const BUILDING_COLORS = [
  0x1a1a2e, // Dark blue
  0x16213e, // Navy
  0x0f0f23, // Near black
  0x1a1a3e, // Deep purple
  0x2a1a2e, // Dark magenta
];

export class BuildingFactory {
  private scene: THREE.Scene;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
  }

  public createBuilding(config: BuildingConfig): BuildingMeshes {
    const style = config.style || 'standard';

    switch (style) {
      case 'tower':
        return this.createTower(config);
      case 'warehouse':
        return this.createWarehouse(config);
      case 'skyscraper':
        return this.createSkyscraper(config);
      default:
        return this.createStandardBuilding(config);
    }
  }

  private createStandardBuilding(config: BuildingConfig): BuildingMeshes {
    const { width, depth, height, position } = config;

    // Main building geometry
    const geometry = new THREE.BoxGeometry(width, height, depth);
    const material = new THREE.MeshStandardMaterial({
      color: BUILDING_COLORS[Math.floor(Math.random() * BUILDING_COLORS.length)],
      metalness: 0.7,
      roughness: 0.3,
    });

    const building = new THREE.Mesh(geometry, material);
    building.position.copy(position);
    building.position.y = height / 2;
    building.castShadow = true;
    building.receiveShadow = true;

    // Neon edge highlights
    const edgeColor = NEON_COLORS[Math.floor(Math.random() * NEON_COLORS.length)];
    const edges = this.createNeonEdges(geometry, edgeColor, position, height);

    // Details (windows, antennas, etc.)
    const details = this.createBuildingDetails(config, edgeColor);

    return { building, edges, details };
  }

  private createTower(config: BuildingConfig): BuildingMeshes {
    const { width, height, position } = config;

    // Tapered tower
    const geometry = new THREE.CylinderGeometry(
      width * 0.3, // top radius
      width * 0.5, // bottom radius
      height,
      8 // segments
    );

    const material = new THREE.MeshStandardMaterial({
      color: 0x1a1a3e,
      metalness: 0.8,
      roughness: 0.2,
    });

    const building = new THREE.Mesh(geometry, material);
    building.position.copy(position);
    building.position.y = height / 2;

    const edgeColor = NEON_COLORS[Math.floor(Math.random() * NEON_COLORS.length)];
    const edges = this.createNeonEdges(geometry, edgeColor, position, height);

    const details = this.createTowerDetails(config, edgeColor);

    return { building, edges, details };
  }

  private createWarehouse(config: BuildingConfig): BuildingMeshes {
    const { width, depth, height, position } = config;

    // Warehouse with slanted roof
    const shape = new THREE.Shape();
    shape.moveTo(-width / 2, 0);
    shape.lineTo(-width / 2, height * 0.7);
    shape.lineTo(0, height);
    shape.lineTo(width / 2, height * 0.7);
    shape.lineTo(width / 2, 0);
    shape.lineTo(-width / 2, 0);

    const extrudeSettings = {
      depth: depth,
      bevelEnabled: false,
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    geometry.rotateY(Math.PI / 2);
    geometry.translate(0, 0, -depth / 2);

    const material = new THREE.MeshStandardMaterial({
      color: 0x2a1a2e,
      metalness: 0.6,
      roughness: 0.4,
    });

    const building = new THREE.Mesh(geometry, material);
    building.position.copy(position);

    const edgeColor = NEON_COLORS[Math.floor(Math.random() * NEON_COLORS.length)];
    const edgesGeom = new THREE.EdgesGeometry(geometry);
    const edgesMat = new THREE.LineBasicMaterial({
      color: edgeColor,
      linewidth: 2,
    });
    const edges = new THREE.LineSegments(edgesGeom, edgesMat);
    edges.position.copy(position);

    const details = this.createWarehouseDetails(config, edgeColor);

    return { building, edges, details };
  }

  private createSkyscraper(config: BuildingConfig): BuildingMeshes {
    const { width, depth, height, position } = config;

    // Multi-section skyscraper
    const group = new THREE.Group();
    const sections = 3 + Math.floor(Math.random() * 2);
    let currentHeight = 0;
    let currentWidth = width;
    let currentDepth = depth;

    const material = new THREE.MeshStandardMaterial({
      color: 0x0f0f23,
      metalness: 0.9,
      roughness: 0.1,
    });

    for (let i = 0; i < sections; i++) {
      const sectionHeight = height / sections;
      const geometry = new THREE.BoxGeometry(currentWidth, sectionHeight, currentDepth);
      const section = new THREE.Mesh(geometry, material.clone());
      section.position.y = currentHeight + sectionHeight / 2;
      group.add(section);

      currentHeight += sectionHeight;
      currentWidth *= 0.85;
      currentDepth *= 0.85;
    }

    // Use a dummy mesh for the main building reference
    const dummyGeom = new THREE.BoxGeometry(width, height, depth);
    const building = new THREE.Mesh(dummyGeom, material);
    building.visible = false;
    building.position.copy(position);
    building.position.y = height / 2;

    group.position.copy(position);
    this.scene.add(group);

    const edgeColor = NEON_COLORS[Math.floor(Math.random() * NEON_COLORS.length)];
    const edges = this.createSkyscraperEdges(group, edgeColor);
    edges.position.copy(position);

    const details = this.createSkyscraperDetails(config, edgeColor, group);

    return { building, edges, details };
  }

  private createNeonEdges(
    geometry: THREE.BufferGeometry,
    color: number,
    position: THREE.Vector3,
    height: number
  ): THREE.LineSegments {
    const edgesGeometry = new THREE.EdgesGeometry(geometry);
    const edgesMaterial = new THREE.LineBasicMaterial({
      color: color,
      linewidth: 2,
    });

    const edges = new THREE.LineSegments(edgesGeometry, edgesMaterial);
    edges.position.copy(position);
    edges.position.y = height / 2;

    return edges;
  }

  private createSkyscraperEdges(group: THREE.Group, color: number): THREE.LineSegments {
    const edgesGroup = new THREE.Group();

    group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const edgesGeom = new THREE.EdgesGeometry(child.geometry);
        const edgesMat = new THREE.LineBasicMaterial({ color });
        const edges = new THREE.LineSegments(edgesGeom, edgesMat);
        edges.position.copy(child.position);
        edgesGroup.add(edges);
      }
    });

    // Return first edges or create dummy
    const dummyGeom = new THREE.EdgesGeometry(new THREE.BoxGeometry(1, 1, 1));
    const dummyMat = new THREE.LineBasicMaterial({ color });
    const dummyEdges = new THREE.LineSegments(dummyGeom, dummyMat);
    dummyEdges.visible = false;

    this.scene.add(edgesGroup);
    return dummyEdges;
  }

  private createBuildingDetails(config: BuildingConfig, neonColor: number): THREE.Group {
    const details = new THREE.Group();
    const { width, depth, height, position } = config;

    // Window strips
    const windowRows = Math.floor(height / 3);
    const windowMaterial = new THREE.MeshStandardMaterial({
      color: neonColor,
      emissive: neonColor,
      emissiveIntensity: 0.3,
      transparent: true,
      opacity: 0.6,
    });

    // Front and back windows
    for (let i = 0; i < windowRows; i++) {
      const windowGeom = new THREE.PlaneGeometry(width * 0.8, 0.5);

      const frontWindow = new THREE.Mesh(windowGeom, windowMaterial);
      frontWindow.position.set(0, 2 + i * 3, depth / 2 + 0.01);
      details.add(frontWindow);

      const backWindow = new THREE.Mesh(windowGeom, windowMaterial.clone());
      backWindow.position.set(0, 2 + i * 3, -depth / 2 - 0.01);
      backWindow.rotation.y = Math.PI;
      details.add(backWindow);
    }

    // Roof accent
    const roofAccent = new THREE.Mesh(
      new THREE.BoxGeometry(width * 0.9, 0.2, depth * 0.9),
      new THREE.MeshStandardMaterial({
        color: neonColor,
        emissive: neonColor,
        emissiveIntensity: 1,
      })
    );
    roofAccent.position.y = height + 0.1;
    details.add(roofAccent);

    details.position.copy(position);
    return details;
  }

  private createTowerDetails(config: BuildingConfig, neonColor: number): THREE.Group {
    const details = new THREE.Group();
    const { height, position } = config;

    // Antenna
    const antennaGeom = new THREE.CylinderGeometry(0.1, 0.1, 5, 8);
    const antennaMat = new THREE.MeshStandardMaterial({
      color: 0x333333,
      metalness: 0.9,
    });
    const antenna = new THREE.Mesh(antennaGeom, antennaMat);
    antenna.position.y = height + 2.5;
    details.add(antenna);

    // Blinking light
    const lightGeom = new THREE.SphereGeometry(0.3, 8, 8);
    const lightMat = new THREE.MeshStandardMaterial({
      color: 0xff0000,
      emissive: 0xff0000,
      emissiveIntensity: 2,
    });
    const light = new THREE.Mesh(lightGeom, lightMat);
    light.position.y = height + 5;
    details.add(light);

    // Ring lights around tower
    const ringCount = Math.floor(height / 10);
    for (let i = 0; i < ringCount; i++) {
      const ringGeom = new THREE.TorusGeometry(config.width * 0.4, 0.1, 8, 16);
      const ringMat = new THREE.MeshStandardMaterial({
        color: neonColor,
        emissive: neonColor,
        emissiveIntensity: 1,
      });
      const ring = new THREE.Mesh(ringGeom, ringMat);
      ring.rotation.x = Math.PI / 2;
      ring.position.y = 5 + i * 10;
      details.add(ring);
    }

    details.position.copy(position);
    return details;
  }

  private createWarehouseDetails(config: BuildingConfig, neonColor: number): THREE.Group {
    const details = new THREE.Group();
    const { width, depth, height, position } = config;

    // Loading dock lights
    const dockLightGeom = new THREE.BoxGeometry(0.5, 0.2, 0.5);
    const dockLightMat = new THREE.MeshStandardMaterial({
      color: neonColor,
      emissive: neonColor,
      emissiveIntensity: 2,
    });

    for (let i = 0; i < 3; i++) {
      const light = new THREE.Mesh(dockLightGeom, dockLightMat);
      light.position.set(-width / 3 + i * (width / 3), 0.5, depth / 2 + 0.3);
      details.add(light);
    }

    // Ventilation units on roof
    for (let i = 0; i < 2; i++) {
      const ventGeom = new THREE.BoxGeometry(2, 1, 2);
      const ventMat = new THREE.MeshStandardMaterial({
        color: 0x333333,
        metalness: 0.8,
      });
      const vent = new THREE.Mesh(ventGeom, ventMat);
      vent.position.set(-width / 4 + i * (width / 2), height * 0.85 + 0.5, 0);
      details.add(vent);
    }

    details.position.copy(position);
    return details;
  }

  private createSkyscraperDetails(
    config: BuildingConfig,
    neonColor: number,
    _buildingGroup: THREE.Group
  ): THREE.Group {
    const details = new THREE.Group();
    const { width, height, position } = config;

    // Spire
    const spireGeom = new THREE.ConeGeometry(width * 0.1, height * 0.2, 4);
    const spireMat = new THREE.MeshStandardMaterial({
      color: 0x333333,
      metalness: 0.9,
    });
    const spire = new THREE.Mesh(spireGeom, spireMat);
    spire.position.y = height + height * 0.1;
    details.add(spire);

    // Top beacon
    const beaconGeom = new THREE.SphereGeometry(0.5, 16, 16);
    const beaconMat = new THREE.MeshStandardMaterial({
      color: neonColor,
      emissive: neonColor,
      emissiveIntensity: 3,
    });
    const beacon = new THREE.Mesh(beaconGeom, beaconMat);
    beacon.position.y = height + height * 0.2 + 0.5;
    details.add(beacon);

    details.position.copy(position);
    return details;
  }

  public addToScene(meshes: BuildingMeshes): void {
    this.scene.add(meshes.building);
    this.scene.add(meshes.edges);
    this.scene.add(meshes.details);
  }
}
